import React from 'react';
import { URL } from '@ux/util';
import { NamespaceConsumer, NamespaceShape, withNamespace } from '@ux/namespace-component';
import PropTypes from 'prop-types';
import Link from '@ux/link';

// Regex for whitespace and non-typographical unicode characters
// eslint-disable-next-line no-control-regex
const WHITESPACE_REGEX = /[\s\u0000-\u001F\u007F-\u009F]/g;

/**
 * Add query parameters from props to the url.
 *
 * @param {String} url link.
 * @param {Object} props Properties to merge with the query parameters object.
 * @returns {String} Configured url.
 * @private
 */
function updateQuery(url, props) {
  url = new URL(url, true);
  return url.set('query', { ...url.query, ...props }).href;
}

/**
 * Renders shared GD logo.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} shared logo
 * @public
 */
export class LogoComponent extends NamespaceConsumer {
  constructor(props) {
    super(props);

    // Some Logo consumers setup breakpoints themselves and will trigger
    // corresponding re-renders of the Logo. But for those that don't, Logo
    // needs to setup breakpoints on its own. This prevents spurious re-renders
    // when consumed by an already-breakpoint-sensitive component.
    this.shouldConfigureBreakpoints = typeof props.isMobile !== 'boolean';
  }

  /**
   * Check if breakpoint is mobile. (Respects `shouldConfigureBreakpoints`.)
   *
   * @returns {Boolean} Mobile or not.
   * @private
   */
  get _isMobile() {
    return this.shouldConfigureBreakpoints
      ? this.isMobile
      : this.props.isMobile;
  }

  /**
   * Get the logo URL for the current breakpoint.
   *
   * @returns {String} Logo URL.
   * @private
   */
  get _logo() {
    const { logos } = this.props;
    const url = logos && (this._isMobile ? logos.mobile : logos.desktop) || '';

    return url.replace(WHITESPACE_REGEX, '');
  }

  componentDidMount() {
    if (this.shouldConfigureBreakpoints) {
      this.breakpoint('mobile', 'phablet', 'tablet', 'desktop', 'large');
    }
  }

  componentWillUnmount() {
    if (this.shouldConfigureBreakpoints) {
      this.breakup();
    }
  }

  /**
   * Return elements for rendering a reseller logo
   * @param {String} imgSrc Logo source url
   * @returns {ReactElement} Part of the shared logo
   * @api private
   */
  resellerLogo() {
    const { spanClass, privateLabelName } = this.props;
    const imgSrc = this._logo;

    //
    // If no URL for a custom logo has been provided then display
    // the name of the private label.
    //
    if (!imgSrc) {
      return (
        <span id='registrar' className={ this.namespace('logo-text')  }>
          { privateLabelName }
        </span>
      );
    }

    // To prevent SVGs from rendering 0x0, set an arbitrarily large enough width
    // to cover the max-width of the span. Only appropriate for SVGs because
    // other image types would stretch if their aspect ratio is narrower than
    // the span's.
    const svgHack = /\.svg$/.test(imgSrc) && {
      width: 200
    };

    return (
      <span className={ this.classNames(this.namespace('logo-img'), spanClass) }>
        <img id='registrar'
          src={ encodeURI(imgSrc) }
          alt={ privateLabelName }
          { ...svgHack } />
      </span>
    );
  }

  /**
   * Work around for `sinon@7`/lolex having issues faking timers.
   * Context
   *
   * @returns {number} number of milliseconds since epoch (same as `Date.now()`)
   */
  getDateNow() {
    return Date.now();
  }

  /**
   * Return elements for rendering the temporary GoDaddy logo for culture campaigns
   *
   * @param {Object} tempLogo from props
   * @param {String} tempLogo.filename the name of the file to be retrieved from CDN
   * @param {Object} tempLogo.start the starting date of the campaign
   * @param {Object} tempLogo.end the ending date of the campaign
   * @param {Object} tempLogo.styles the styles to be applied
   * @returns {ReactElement} The temp logo
   */
  renderTempLogo({ filename, start, end, styles = {}, altText = '' }) {
    const props = this.props;

    const convertToDateObject = ({ year, month, day, hour = null }) => {
      // subtract 1 from the month since Date months are indexed from 0
      return new Date(year, month - 1, day, hour);
    };

    const startDate = convertToDateObject(start);
    const endDate = convertToDateObject(end);

    const now = this.getDateNow();

    if (startDate < now && now < endDate) {
      const cdn = `${props.urls.cdn.href}ux/sales-header/`;

      return (
        <span id={ props.id || 'homepage' } className={ props.spanClass } aria-label='GoDaddy'>
          <img
            alt={ altText }
            src={ `${cdn}${filename}` }
            onError={ (e) => {
              e.target.onError = null;
              // default logo fallback
              // TODO: update default logo fallback
              e.target.src = `${cdn}gd-logo.svg`;
              e.target.style = null;
            } }
            style={ styles }
          />
        </span>
      );
    }

    return this.renderGoLogo();
  }

  renderGoLogo() {
    const props = this.props;

    return (
      <React.Fragment>
        <span id={ props.id || 'homepage' } className={ this.classNames(
          this.namespace('mobile-logo'),
          this.namespace('go-logo'),
          props.spanClass
        ) } aria-label='GoDaddy'>
          <svg className={ this.namespace('svg-fill-black') } viewBox='0 0 36 32' xmlns='http://www.w3.org/2000/svg'>
            <path d='M30.824 1.453c-3.713-2.316-8.603-1.77-12.963.972-4.347-2.74-9.239-3.288-12.948-.972C-.955 5.12-1.667 14.564 3.32 22.546c3.677 5.887 9.428 9.335 14.548 9.271 5.119.064 10.87-3.384 14.547-9.27C37.4 14.563 36.692 5.12 30.824 1.452zM6.02 20.863a19.112 19.112 0 01-2.303-5.273 15.067 15.067 0 01-.5-4.97C3.44 7.673 4.64 5.375 6.598 4.15c1.957-1.224 4.544-1.295 7.299-.203.419.169.829.358 1.228.568a22.496 22.496 0 00-3.956 4.754C8.14 14.116 7.215 19.511 8.272 23.81a19.723 19.723 0 01-2.253-2.947zm26.003-5.273a19.158 19.158 0 01-2.302 5.272 19.733 19.733 0 01-2.254 2.954c.946-3.855.303-8.576-2.014-13.005a.583.583 0 00-.837-.228l-7.225 4.515a.59.59 0 00-.188.813l1.06 1.695a.59.59 0 00.814.188l4.683-2.925c.151.454.303.901.416 1.363.442 1.617.611 3.297.5 4.97-.223 2.95-1.424 5.247-3.38 6.471a6.607 6.607 0 01-3.35.97h-.15a6.599 6.599 0 01-3.35-.97c-1.958-1.224-3.159-3.521-3.381-6.472-.11-1.672.058-3.352.5-4.969a19.671 19.671 0 016.03-9.65 15.108 15.108 0 014.241-2.629c2.748-1.092 5.34-1.02 7.298.203 1.958 1.224 3.157 3.522 3.38 6.47a15.105 15.105 0 01-.49 4.964zM27.89 30.386v-.21c0-.015.005-.03.015-.04a.048.048 0 01.038-.015h1.342c.014-.001.028.004.038.015.01.01.016.025.015.04v.21a.051.051 0 01-.015.04.048.048 0 01-.038.014h-.49v1.322a.05.05 0 01-.017.04.051.051 0 01-.038.017h-.253a.055.055 0 01-.038-.017.06.06 0 01-.015-.04V30.44h-.49a.048.048 0 01-.039-.015.051.051 0 01-.015-.04zm2.345-.217l.374.848.375-.848a.073.073 0 01.03-.036.08.08 0 01.05-.012h.4a.051.051 0 01.053.053v1.592a.056.056 0 01-.013.037.05.05 0 01-.037.016h-.24a.055.055 0 01-.038-.016.056.056 0 01-.015-.037v-1.203l-.393.874a.09.09 0 01-.03.04.086.086 0 01-.05.013h-.194a.085.085 0 01-.048-.012.09.09 0 01-.03-.041l-.393-.874v1.203a.05.05 0 01-.015.037.053.053 0 01-.036.016h-.243a.05.05 0 01-.036-.016.056.056 0 01-.015-.037v-1.592a.05.05 0 01.017-.038.047.047 0 01.036-.015h.413a.083.083 0 01.049.012.071.071 0 01.029.036z'/>
          </svg>
        </span>
        <span id={ props.id || 'homepage' } className={ this.classNames(
          this.namespace('desktop-logo'),
          this.namespace('go-logo'),
          props.spanClass
        ) } aria-label='GoDaddy'>
          <svg className={ this.namespace('svg-fill-black') } viewBox='0 0 166 34' xmlns='http://www.w3.org/2000/svg'>
            <path d='M32.937 1.554c-3.968-2.48-9.193-1.889-13.852 1.039C14.44-.335 9.213-.925 5.25 1.553c-6.27 3.919-7.032 14.01-1.701 22.54 3.93 6.289 10.074 9.974 15.544 9.906 5.47.068 11.615-3.617 15.545-9.906 5.324-8.53 4.568-18.621-1.701-22.54zM6.43 22.292a20.434 20.434 0 01-2.46-5.632 16.104 16.104 0 01-.534-5.31c.238-3.153 1.521-5.608 3.612-6.914s4.855-1.385 7.8-.217c.441.177.878.38 1.312.606a24.09 24.09 0 00-4.228 5.081C8.697 15.086 7.71 20.848 8.84 25.443a20.911 20.911 0 01-2.408-3.151zm27.786-5.634a20.482 20.482 0 01-2.46 5.632 21.1 21.1 0 01-2.408 3.158c1.01-4.12.324-9.165-2.153-13.897a.625.625 0 00-.895-.243l-7.72 4.823a.631.631 0 00-.2.87l1.133 1.811a.63.63 0 00.869.2l5.004-3.126c.162.486.324.971.445 1.457a16.1 16.1 0 01.536 5.303c-.238 3.151-1.521 5.606-3.612 6.914a7.06 7.06 0 01-3.579 1.036h-.16a7.051 7.051 0 01-3.578-1.036c-2.093-1.308-3.376-3.763-3.614-6.914a16.143 16.143 0 01.534-5.31 21.015 21.015 0 016.444-10.314 16.137 16.137 0 014.532-2.806c2.936-1.168 5.705-1.09 7.797.217 2.093 1.308 3.375 3.761 3.613 6.914a16.145 16.145 0 01-.528 5.311zm39.848-3.766c-4.06 0-7.34 3.169-7.34 7.2 0 4.004 3.28 7.121 7.34 7.121 4.086 0 7.366-3.112 7.366-7.12 0-4.03-3.275-7.2-7.366-7.2zm0 10.557c-1.871 0-3.295-1.513-3.295-3.384s1.424-3.407 3.295-3.407c1.898 0 3.322 1.54 3.322 3.412 0 1.87-1.424 3.385-3.322 3.385zM90.583 7.362h-7.468a.6.6 0 00-.61.612v18.208a.605.605 0 00.61.648h7.468c5.977 0 10.13-3.975 10.13-9.758 0-5.818-4.153-9.71-10.13-9.71zm.177 15.622h-4.087V11.198h4.087c3.308 0 5.588 2.474 5.588 5.866 0 3.336-2.28 5.92-5.588 5.92zm24.82-9.7h-2.809a.633.633 0 00-.582.612v.833c-.64-1.057-2.085-1.835-3.884-1.835-3.503 0-6.783 2.751-6.783 7.145 0 4.37 3.251 7.171 6.755 7.171 1.806 0 3.28-.777 3.92-1.834v.861a.587.587 0 00.582.585h2.808a.571.571 0 00.584-.585V13.896a.594.594 0 00-.592-.612zm-6.533 10.196c-1.86 0-3.256-1.43-3.256-3.412s1.397-3.41 3.256-3.41c1.86 0 3.257 1.426 3.257 3.408s-1.395 3.412-3.257 3.412zm22.294-16.118h-2.808a.592.592 0 00-.61.584v6.728c-.648-1.002-2.114-1.78-3.948-1.78-3.476 0-6.7 2.751-6.7 7.145 0 4.37 3.252 7.171 6.755 7.171 1.806 0 3.17-.777 3.92-1.834v.861c0 .322.261.583.583.585h2.808a.57.57 0 00.584-.585V7.95a.57.57 0 00-.584-.588zm-6.532 16.152c-1.852 0-3.237-1.444-3.237-3.448s1.39-3.447 3.237-3.447c1.846 0 3.237 1.444 3.237 3.447s-1.384 3.448-3.237 3.448zm22.29-16.152h-2.803a.594.594 0 00-.612.584v6.728c-.64-1.002-2.114-1.78-3.947-1.78-3.477 0-6.7 2.751-6.7 7.145 0 4.37 3.253 7.171 6.755 7.171 1.807 0 3.168-.777 3.92-1.834v.861a.587.587 0 00.584.585h2.803a.568.568 0 00.582-.585V7.95a.568.568 0 00-.582-.588zm-6.532 16.152c-1.853 0-3.237-1.444-3.237-3.448s1.39-3.447 3.237-3.447c1.846 0 3.237 1.444 3.237 3.447s-1.38 3.448-3.232 3.448zm21.906-9.283l-4.19 14.37c-.809 2.556-2.613 4.086-5.421 4.086-1.277 0-2.44-.261-3.35-.782-.531-.303-.971-.58-.971-1.023 0-.275.089-.417.25-.675l.832-1.246c.235-.348.408-.461.66-.461a.96.96 0 01.554.192c.523.339 1.008.63 1.748.63.864 0 1.524-.277 1.88-1.306l.36-1.193h-1.696c-.418 0-.648-.249-.751-.584l-3.75-12.008c-.14-.473-.011-.946.683-.946h2.954c.36 0 .613.123.771.64l2.77 9.67 2.589-9.67c.082-.334.306-.64.75-.64h2.802c.552-.001.719.387.526.946zm-96.806 4.274v7.676a.625.625 0 01-.635.634h-2.317a.623.623 0 01-.634-.634v-2.015c-1.472 1.858-4.03 3.028-6.924 3.028-5.434 0-9.681-4.088-9.681-9.908 0-6.048 4.585-10.217 10.377-10.217 4.276 0 7.694 1.839 9.212 5.537a.843.843 0 01.07.309c0 .175-.116.307-.486.435l-2.706 1.042a.694.694 0 01-.511.009.783.783 0 01-.324-.371c-.971-1.847-2.7-3.1-5.36-3.1-3.45 0-5.922 2.694-5.922 6.188 0 3.387 2.104 6.172 6.02 6.172 2.06 0 3.703-.97 4.469-2.037H57.87a.625.625 0 01-.634-.635v-2.086a.625.625 0 01.634-.634h7.161a.604.604 0 01.635.607zm96.882-8.063v-.186c0-.013.005-.026.013-.036a.052.052 0 01.034-.013h1.183c.013 0 .025.005.034.013.009.01.013.023.013.036v.186a.052.052 0 01-.013.034.047.047 0 01-.034.013h-.432v1.167a.05.05 0 01-.048.048h-.224a.044.044 0 01-.032-.014.047.047 0 01-.015-.034v-1.167h-.432a.044.044 0 01-.047-.047zm2.069-.193l.323.75.324-.75a.066.066 0 01.026-.032.087.087 0 01.044-.01h.375a.044.044 0 01.047.046v1.404a.044.044 0 01-.045.047h-.21a.047.047 0 01-.035-.013.042.042 0 01-.013-.034v-1.06l-.348.77a.084.084 0 01-.026.038.08.08 0 01-.043.01h-.172a.078.078 0 01-.042-.01.073.073 0 01-.026-.037l-.346-.77v1.06a.052.052 0 01-.013.033.052.052 0 01-.032.013h-.216a.042.042 0 01-.03-.013.042.042 0 01-.013-.034v-1.404a.044.044 0 01.047-.046h.364c.014 0 .029.003.042.01.009.009.015.02.018.032z'/>
          </svg>
        </span>
      </React.Fragment>
    );
  }

  render() {
    const props = this.props;
    const linkProps = {
      'className': this.classNames(props.className, {
        [this.namespace('white-logo-bg')]: props.privateLabelId !== 1 && this._logo
      }),
      'urlArgs': props.urlArgs,
      'href': updateQuery(props.href, props.urlArgs),
      'data-eid': props['data-eid']
    };

    let logoContent;

    if (props.privateLabelId !== 1) {
      linkProps.rel = 'nofollow';
      logoContent = this.resellerLogo();
    } else if (props.tempLogo) {
      logoContent = this.renderTempLogo(props.tempLogo);
    } else {
      logoContent = this.renderGoLogo();
    }

    const logo = (
      <React.Fragment>
        {
          props.market !== 'en-US'
            && props.showCountryNameOnLogo
            && props.privateLabelId === 1
            && <span className={ this.namespace('topnav-country') }>
              { props.country }
            </span>
        }
        { logoContent }
      </React.Fragment>
    );

    if (!props.href) return logo;

    return (
      <Link { ...linkProps }>
        { logo }
      </Link>
    );
  }
}

LogoComponent.defaultProps = {
  country: '',
  market: 'en-US',
  privateLabelId: 1,
  showCountryNameOnLogo: false,
  urlArgs: {}
};

LogoComponent.propTypes = {
  ...NamespaceShape,
  'className': PropTypes.string,
  'country': PropTypes.string,
  'data-eid': PropTypes.string,
  'href': PropTypes.string,
  'logos': PropTypes.object,
  'market': PropTypes.string.isRequired,
  'messages': PropTypes.object.isRequired,
  'privateLabelId': PropTypes.number.isRequired,
  'privateLabelName': PropTypes.string,
  'showCountryNameOnLogo': PropTypes.bool,
  'spanClass': PropTypes.string,
  'tempLogo': PropTypes.object,
  'urlArgs': PropTypes.object,
  'urls': PropTypes.object,
  'isMobile': PropTypes.bool,
  'id': PropTypes.string
};

export default withNamespace(LogoComponent);
